// One person's week (round 44, spec 13).
//
// The page never decides what is allowed: the server's `can` says whether this
// person may edit, submit, submit for the worker, approve, send back, add a
// document, open the PDF or delete, and the buttons follow it.
//
// Who sees which mode:
//   the week's own worker, while it is a draft or sent back  -> the editor,
//     autosaving, with Sign and submit;
//   the office                                              -> the week read,
//     with Approve and Send back, and Edit for a correction (an explicit
//     Save, logged, and the worker sees it);
//   anyone else allowed to look                              -> the week read.
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ApiError } from '../api';
import { useSession } from '../session';
import { Banner } from '../ui';
import { PageHeader } from '../components/ui/Page';
import { Modal } from '../components/ui/Modal';
import { ActionMenu } from '../components/ui/ActionMenu';
import { StatusBadge } from '../components/ui/StatusBadge';
import { useConfirm } from '../components/ui/confirm';
import { Documents } from '../components/Documents';
import { PdfViewer } from '../components/PdfViewer';
import { useTabParam } from '../lib/useTabParam';
import { formatMomentAu } from '../lib/dates';
import { WeekEditor, type SaveState } from '../components/timesheets/WeekEditor';
import { WeekReadView } from '../components/timesheets/WeekReadView';
import { TimesheetHistory } from '../components/timesheets/TimesheetHistory';
import { SignAndSubmitModal } from '../components/timesheets/SignAndSubmitModal';
import { timesheets, type TimesheetDetail } from '../timesheetsApi';

const TABS = [
  { key: 'week', label: 'Week' },
  { key: 'documents', label: 'Documents' },
  { key: 'history', label: 'History' },
] as const;
const TAB_KEYS = TABS.map((t) => t.key);

const STALE_MESSAGE =
  'Someone else changed this timesheet while you had it open. Reload to see their changes, then make yours again.';

export function TimesheetDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const { user } = useSession();
  const [detail, setDetail] = useState<TimesheetDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [stale, setStale] = useState(false);
  const [tab, setTab] = useTabParam(TAB_KEYS);
  const [officeEditing, setOfficeEditing] = useState(false);
  const [signing, setSigning] = useState(false);
  const [sendingBack, setSendingBack] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  // The PDF opens INSIDE the app: a link straight to it replaced a Home
  // Screen app with the file and no way back (Nathan, 2026-10-09).
  const [viewingPdf, setViewingPdf] = useState(false);
  // The office editor's save state, read when leaving edit mode.
  const editorState = useRef<SaveState>('saved');
  const [modalError, setModalError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!id) return;
    try {
      setDetail(await timesheets.detail(id));
      setError(null);
      setStale(false);
    } catch (err) {
      setError(err instanceof ApiError && err.status === 404 ? 'That timesheet is not there.' : 'Could not load that timesheet.');
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  if (!detail || !user) {
    return error ? <Banner kind="error">{error}</Banner> : <p className="muted">Loading…</p>;
  }

  const { sheet, can, worker } = detail;
  const owner = user.id === sheet.userId;
  const workerEditing = owner && can.edit;
  const editing = workerEditing || officeEditing;

  async function run(action: () => Promise<TimesheetDetail | void>, success?: string) {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const next = await action();
      if (next) setDetail(next);
      if (success) setNotice(success);
      return true;
    } catch (err) {
      if (err instanceof ApiError && err.status === 409 && (err.details as { code?: string })?.code === 'stale') {
        setStale(true);
      } else {
        setError(err instanceof ApiError ? err.message : 'Something went wrong. Try again.');
      }
      return false;
    } finally {
      setBusy(false);
    }
  }

  async function approve() {
    await run(() => timesheets.approve(sheet.id, detail!.sheet.lockVersion), `Approved. ${worker.name} has been emailed.`);
  }

  async function sendBack() {
    setModalError(null);
    if (reason.trim() === '') {
      setModalError('Say what needs fixing, so they know what to change.');
      return;
    }
    const ok = await run(() => timesheets.sendBack(sheet.id, detail!.sheet.lockVersion, reason.trim()), `Sent back to ${worker.name}, who has been emailed.`);
    if (ok) {
      setSendingBack(false);
      setReason('');
    }
  }

  async function submitForWorker(flush: () => Promise<TimesheetDetail | null>) {
    const ok = await confirm({
      title: `Submit this week for ${worker.name}?`,
      message: `It goes in without their signature and is marked as entered by the office, on the PDF and in the export. ${worker.name} can read it but not change it.`,
      confirmLabel: 'Submit for them',
      tone: 'warning',
    });
    if (!ok) return;
    const saved = await flush();
    const lock = saved?.sheet.lockVersion ?? detail!.sheet.lockVersion;
    const done = await run(() => timesheets.submit(sheet.id, lock, null), 'Submitted for them, marked as entered by the office.');
    if (done) setOfficeEditing(false);
  }

  async function signAndSubmit(signature: Blob, lock: number) {
    setModalError(null);
    setBusy(true);
    try {
      setDetail(await timesheets.submit(sheet.id, lock, signature));
      setSigning(false);
      setNotice('Submitted. The office will check it, and you will get an email when it is approved.');
    } catch (err) {
      if (err instanceof ApiError && err.status === 409 && (err.details as { code?: string })?.code === 'stale') {
        setSigning(false);
        setStale(true);
      } else {
        setModalError(err instanceof ApiError ? err.message : 'Could not submit. Check your connection and try again.');
      }
    } finally {
      setBusy(false);
    }
  }

  /** Leaving the office editor: never silently, with a change unsaved. */
  async function doneEditing() {
    const unsaved = editorState.current !== 'saved' && editorState.current !== 'saving';
    if (unsaved) {
      const ok = await confirm({
        title: 'Leave without saving?',
        message: 'The changes you made to this week have not been saved, and will be lost.',
        confirmLabel: 'Leave without saving',
        tone: 'warning',
      });
      if (!ok) return;
    }
    editorState.current = 'saved';
    setOfficeEditing(false);
  }

  async function remove() {
    const ok = await confirm({
      title: 'Delete this week?',
      message: 'It was never submitted, so nothing is lost but what was typed in. The person can start it again.',
      confirmLabel: 'Delete',
      tone: 'danger',
    });
    if (!ok) return;
    if (await run(() => timesheets.remove(sheet.id))) navigate('/app/timesheets', { replace: true });
  }

  return (
    <>
      <PageHeader
        title={owner ? 'My timesheet' : `Timesheet · ${worker.name}`}
        subtitle={
          <>
            Week of {sheet.weekLabel}
            {worker.position && !owner && <> · {worker.position}</>}
          </>
        }
        actions={
          <>
            <StatusBadge kind="timesheet" status={sheet.status} />
            {can.approve && !editing && (
              <button type="button" className="btn-primary" disabled={busy} onClick={() => void approve()}>
                Approve
              </button>
            )}
            {can.return && !editing && (
              <button type="button" className="btn-secondary" disabled={busy} onClick={() => setSendingBack(true)}>
                Send back
              </button>
            )}
            {can.edit && !owner && !officeEditing && (
              <button type="button" className="btn-secondary" disabled={busy} onClick={() => setOfficeEditing(true)}>
                Edit
              </button>
            )}
            {can.downloadPdf && (
              <button type="button" className="btn-secondary" onClick={() => setViewingPdf(true)}>
                View PDF
              </button>
            )}
            <Link to="/app/timesheets" className="btn-secondary">
              {owner ? 'My weeks' : 'All timesheets'}
            </Link>
            {can.delete && (
              <ActionMenu label="More actions" items={[{ label: 'Delete this week', tone: 'danger', onClick: () => void remove() }]} />
            )}
          </>
        }
      />

      {stale && (
        <Banner kind="error">
          {STALE_MESSAGE}{' '}
          <button type="button" className="row-action" onClick={() => void load()}>
            Reload
          </button>
        </Banner>
      )}
      {error && <Banner kind="error">{error}</Banner>}
      {notice && <Banner kind="ok">{notice}</Banner>}

      {sheet.status === 'returned' && sheet.returnReason && (
        <section className="panel attention" role="status">
          <p className="!mb-1 font-medium text-gray-900">
            {owner ? `${sheet.returnedByName} sent this back to you` : `Sent back by ${sheet.returnedByName}`}
            {sheet.returnedAt && <span className="muted"> · {formatMomentAu(sheet.returnedAt)}</span>}
          </p>
          <p className="!mb-0 text-gray-800">“{sheet.returnReason}”</p>
          {owner && can.edit && <p className="hint !mb-0 mt-2">Fix it below, then sign and submit it again.</p>}
        </section>
      )}
      {owner && sheet.status === 'submitted' && (
        <p className="banner-info mb-4" role="status">
          This week is with the office. If something needs changing, ask them to send it back.
        </p>
      )}
      {sheet.officeEntered && sheet.status !== 'draft' && (
        <p className="banner-info mb-4" role="status">
          Entered by the office ({sheet.submittedByName}), not signed by {owner ? 'you' : worker.name}.
        </p>
      )}
      {officeEditing && (
        <p className="banner-info mb-4" role="status">
          You are correcting {worker.name}’s week. Your changes are logged with what they were before, and{' '}
          {worker.name} can see them.{' '}
          <button type="button" className="row-action" onClick={() => void doneEditing()}>
            Done editing
          </button>
        </p>
      )}

      <div className="mb-4 flex gap-1 overflow-x-auto border-b border-gray-200" role="tablist">
        {TABS.map((section) => {
          const active = tab === section.key;
          const count =
            section.key === 'history'
              ? detail.events.length
              : section.key === 'documents'
                ? detail.documentCount
                : null;
          return (
            <button
              key={section.key}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => setTab(section.key)}
              className={`min-h-11 whitespace-nowrap border-b-2 px-3 text-sm ${
                active ? 'border-brand-700 font-medium text-brand-800' : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              {section.label}
              {count !== null && count > 0 && <span className="muted"> ({count})</span>}
            </button>
          );
        })}
      </div>

      {/* Mounted while another tab is open, so a look at the Documents tab
          never loses what was typed (the quote pricing tab's rule). */}
      <div hidden={tab !== 'week'}>
        {editing ? (
          <WeekEditor
            // A fresh editor for each mode and each status: the page's own
            // typing never survives into a different situation.
            key={`${sheet.id}:${officeEditing ? 'office' : 'worker'}:${sheet.status}`}
            detail={detail}
            mode={workerEditing ? 'worker' : 'office'}
            onSaved={(next) => setDetail(next)}
            onStale={() => setStale(true)}
            onStateChange={(state) => {
              editorState.current = state;
            }}
            footer={({ flush, state }) => {
              if (workerEditing && can.submit) {
                return (
                  <button
                    type="button"
                    className="btn-primary btn-sm"
                    disabled={busy || state === 'blocked' || state === 'saving'}
                    onClick={async () => {
                      const saved = await flush();
                      if (saved) setDetail(saved);
                      setModalError(null);
                      setSigning(true);
                    }}
                  >
                    Sign and submit
                  </button>
                );
              }
              if (officeEditing && can.submitForWorker) {
                return (
                  <button
                    type="button"
                    className="btn-primary btn-sm"
                    disabled={busy || state === 'blocked'}
                    onClick={() => void submitForWorker(flush)}
                  >
                    Submit for {worker.name.split(' ')[0]}
                  </button>
                );
              }
              return null;
            }}
          />
        ) : (
          <>
            <WeekReadView detail={detail} />
            <Attestation detail={detail} owner={owner} />
          </>
        )}
      </div>

      {tab === 'documents' && (
        <Documents
          kind="timesheet"
          ownerId={sheet.id}
          title="Documents for this week"
          canUploadHere={can.uploadFile}
          uploadHint={
            owner
              ? 'A doctor’s certificate or similar, as a PDF or a photo. Only you and the office can see it.'
              : 'A certificate or similar for this week, as a PDF or a photo. Only the office and the worker can see it.'
          }
        />
      )}

      {tab === 'history' && <TimesheetHistory detail={detail} />}

      {signing && (
        <SignAndSubmitModal
          weekLabel={sheet.weekLabel}
          totals={detail.totals}
          busy={busy}
          error={modalError}
          onClose={() => setSigning(false)}
          onSubmit={(png) => void signAndSubmit(png, detail.sheet.lockVersion)}
        />
      )}

      {viewingPdf && (
        <PdfViewer
          title={`Timesheet · ${worker.name} · week of ${sheet.weekLabel}`}
          src={timesheets.pdfUrl(sheet.id)}
          downloadHref={timesheets.pdfUrl(sheet.id, true)}
          onClose={() => setViewingPdf(false)}
        />
      )}

      {sendingBack && (
        <Modal
          title="Send this week back"
          onClose={() => setSendingBack(false)}
          footer={
            <>
              <button type="button" className="btn-ghost" onClick={() => setSendingBack(false)} disabled={busy}>
                Cancel
              </button>
              <button type="button" className="btn-primary" onClick={() => void sendBack()} disabled={busy}>
                Send back
              </button>
            </>
          }
        >
          <p>
            {worker.name} is emailed what you write here, fixes the week, and signs it again.
            {sheet.status === 'approved' && ' This also takes back the approval.'}
          </p>
          <label className="form-label" htmlFor="return-reason">
            What needs fixing
          </label>
          <textarea
            id="return-reason"
            className="form-input min-h-28 w-full"
            maxLength={2000}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
          {modalError && (
            <p className="mt-2 text-sm text-red-700" role="alert">
              {modalError}
            </p>
          )}
        </Modal>
      )}
    </>
  );
}

/** Who stands behind the hours, under the week. */
function Attestation({ detail, owner }: { detail: TimesheetDetail; owner: boolean }) {
  const { sheet } = detail;
  if (sheet.status === 'draft') {
    return (
      <p className="muted">
        {owner ? 'Not submitted yet.' : `Not submitted yet. Edit it to enter the week for ${detail.worker.name}.`}
      </p>
    );
  }
  return (
    <section className="panel">
      <h2>Sign-off</h2>
      <dl className="details">
        {sheet.signed && sheet.signedAt ? (
          <>
            <dt>Signed by</dt>
            <dd>
              {sheet.signedByName} · {formatMomentAu(sheet.signedAt)}
            </dd>
          </>
        ) : sheet.officeEntered && sheet.submittedAt ? (
          <>
            <dt>Entered by</dt>
            <dd>
              {sheet.submittedByName}, for the office · {formatMomentAu(sheet.submittedAt)} (not signed)
            </dd>
          </>
        ) : null}
        {sheet.approvedAt && (
          <>
            <dt>Approved by</dt>
            <dd>
              {sheet.approvedByName} · {formatMomentAu(sheet.approvedAt)}
            </dd>
          </>
        )}
      </dl>
    </section>
  );
}
